import { type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, HeadContent, Outlet, useMatch } from '@tanstack/react-router';
import React, { useEffect } from 'react';

import { Layout, SelectLoginPage } from '../auth';
import { type AuthContextType, useAuth } from '../context/AuthContext';
import { type FeatureFlagsContextValue } from '../featureFlags/context';
import { Loader } from '../modules/Loader/main';

export interface RouterContext {
  queryClient: QueryClient;
  authContext: AuthContextType;
  /** Feature flag state, for a loader that hides a whole route behind a flag. */
  featureFlags: FeatureFlagsContextValue;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const auth = useAuth();

  const isPopupRoute = useMatch({
    from: '/softphone/popup',
    shouldThrow: false,
  });

  const isStripeInvoiceRoute = useMatch({
    from: '/stripe/success/$token',
    shouldThrow: false,
  });

  const isDispatchMapRoute = useMatch({
    from: '/dispatch-map',
    shouldThrow: false,
  });

  const isLogoutRoute = useMatch({
    from: '/logout',
    shouldThrow: false,
  });

  const isLoginRoute = useMatch({
    from: '/',
    shouldThrow: false,
  });

  const isPaymentRoute = useMatch({
    from: '/payment/$token',
    shouldThrow: false,
  });

  const isAcceptProposalRoute = useMatch({
    from: '/accept-proposal/$token',
    shouldThrow: false,
  });

  // List of routes that don't require authentication
  const publicRoutes =
    isPopupRoute ||
    isStripeInvoiceRoute ||
    isLogoutRoute ||
    isLoginRoute ||
    isPaymentRoute ||
    isAcceptProposalRoute;

  // Direct check and redirect for unauthenticated users - using ref to prevent multiple redirects
  const hasRedirected = React.useRef(false);

  useEffect(() => {
    // Only redirect if:
    // 1. Auth is checked
    // 2. User ID is 0 (not authenticated)
    // 3. Not on a public route
    // 4. Haven't already redirected
    if (auth.authChecked && auth.user.id === 0 && !publicRoutes && !hasRedirected.current) {
      console.log('User not authenticated, redirecting to login page');
      hasRedirected.current = true;
      window.location.replace('/');
    }
  }, [auth.authChecked, auth.user.id, publicRoutes]);

  // Reset the redirection flag if auth status changes
  useEffect(() => {
    if (auth.user.id !== 0) {
      hasRedirected.current = false;
    }
  }, [auth.user.id]);

  // If auth is still checking, show loader
  if (!auth.authChecked) {
    return <Loader />;
  }

  // If on the root route and not authenticated, show login screen
  if (isLoginRoute && auth.user.id === 0) {
    return <SelectLoginPage />;
  }

  // User is authenticated or on a public route
  return (
    <>
      <HeadContent />
      {isPopupRoute ||
      isStripeInvoiceRoute ||
      isDispatchMapRoute ||
      isPaymentRoute ||
      isAcceptProposalRoute ? (
        <Outlet />
      ) : (
        <Layout>
          <Outlet />
        </Layout>
      )}
    </>
  );
}
